import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAuth } from '../../contexts/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

interface ReadingLevelData {
  [level: string]: number;
}

const ReadingLevelChart: React.FC = () => {
  const { token } = useAuth();
  const [data, setData] = useState<ReadingLevelData>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReadingLevelData();
  }, []);

  const fetchReadingLevelData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/charts/reading-levels', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch reading level data: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setData(result.data);
        setError(null);
      } else {
        throw new Error(result.error || 'Failed to fetch data');
      }
    } catch (err) {
      console.error('Error fetching reading level data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const chartData = {
    labels: Object.keys(data).sort((a, b) => {
      // Sort by level number, with "Not Set" at the end
      if (a === 'Not Set') return 1;
      if (b === 'Not Set') return -1;
      
      const numA = parseInt(a.replace('Level ', ''));
      const numB = parseInt(b.replace('Level ', ''));
      return numA - numB;
    }),
    datasets: [
      {
        label: 'Number of Students',
        data: Object.keys(data)
          .sort((a, b) => {
            if (a === 'Not Set') return 1;
            if (b === 'Not Set') return -1;
            const numA = parseInt(a.replace('Level ', ''));
            const numB = parseInt(b.replace('Level ', ''));
            return numA - numB;
          })
          .map(level => data[level]),
        backgroundColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#C9CBCF',
          '#4BC0C0',
          '#FF6384'
        ],
        borderColor: [
          '#FF6384',
          '#36A2EB',
          '#FFCE56',
          '#4BC0C0',
          '#9966FF',
          '#FF9F40',
          '#FF6384',
          '#C9CBCF',
          '#4BC0C0',
          '#FF6384'
        ],
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'Student Distribution by Reading Level',
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        color: '#2c3e50',
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: '#ddd',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          color: '#666',
          font: {
            size: 12,
          }
        },
        grid: {
          color: '#f0f0f0',
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Number of Students',
          color: '#666',
          font: {
            size: 14,
            weight: 'bold' as const,
          }
        }
      },
      x: {
        ticks: {
          color: '#666',
          font: {
            size: 12,
          }
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        title: {
          display: true,
          text: 'Reading Level',
          color: '#666',
          font: {
            size: 14,
            weight: 'bold' as const,
          }
        }
      },
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutCubic' as const,
      delay: (context: any) => context.dataIndex * 200,
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading reading level data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-error">
        <p>Error loading chart: {error}</p>
        <button onClick={fetchReadingLevelData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (Object.keys(data).length === 0) {
    return (
      <div className="chart-empty">
        <p>No reading level data available</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-wrapper">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ReadingLevelChart;
